import React, { useMemo } from 'react';

import RatingsReview from '@rover/components/src/RatingsReview/RatingsReview';
import DogTraining from '@rover/icons/svg/dog-training.svg';
import Map from '@rover/icons/svg/map.svg';
import Trophy from '@rover/icons/svg/trophy.svg';
import { Flex, Text } from '@rover/kibble/core';
import {
  VirtualOnlyBadgeIcon,
  VirtualOnlyBadgeText,
} from '@rover/react-lib/src/pages/training/components/VirtualOnlyBadge';
import type { TrainerSearchResult } from '@rover/rsdk/src/apiClient/latest/model/trainerSearchResult';
import { sentenceCase } from '@rover/utilities/string';

const ICON_SIZE = 16;

type Props = Pick<
  TrainerSearchResult,
  | 'reviewsCount'
  | 'yearsOfExperience'
  | 'ratingsAverage'
  | 'city'
  | 'state'
  | 'zip'
  | 'trainerMethodology'
  | 'trainerMethodologyCustom'
  | 'virtualOnly'
>;

const TrainerStatsSection: React.FC<Props> = ({
  reviewsCount,
  yearsOfExperience,
  ratingsAverage,
  city,
  state,
  zip,
  trainerMethodology,
  trainerMethodologyCustom,
  virtualOnly,
}: Props) => {
  const location = [city, state, zip].filter(Boolean).join(', ');

  const methodologyText = useMemo(() => {
    const isCustomMethodology = !!trainerMethodologyCustom;
    const caseFormatted = sentenceCase(trainerMethodology ?? trainerMethodologyCustom ?? '');
    // DEV-104195: Translate the two texts below
    const formattedText = isCustomMethodology ? `Other: ${caseFormatted}` : caseFormatted;
    return formattedText ? `${formattedText} trainer` : '';
  }, [trainerMethodology, trainerMethodologyCustom]);

  const shouldShowRatingsReviews = reviewsCount > 0;
  const ratingsAverageSr = ratingsAverage ?? '';
  const shouldShowYearsOfExperience = yearsOfExperience !== '';
  const shouldShowLocation = !virtualOnly && location !== '';

  return (
    <Flex flexDirection="column" gap="1x" flex="1" minHeight="82px">
      {/* minHeight calculated by lineHeight * numOfRows + (flexGap * (numOfRows - 1)) */}
      {/*                            17.5          4        1x => 4px         3        */}
      {/* 17.5*4 + 4*3 => 82px */}
      {/* 1. Reviews and count */}
      {shouldShowRatingsReviews && (
        <Flex gap="1x" flexBasis="auto">
          {/* For better text alignment, this is a placeholder for the star icon if it is not shown */}
          {!ratingsAverageSr && <span style={{ height: ICON_SIZE, width: ICON_SIZE }} />}
          <RatingsReview
            fontWeight="regular"
            ratingsAverage={ratingsAverageSr}
            reviewsCount={reviewsCount}
          />
        </Flex>
      )}
      {/* 2. R+ (positive reinforcement) */}
      <Flex gap="1x" flexBasis="auto" data-testid="methodology-row">
        <DogTraining height={ICON_SIZE} width={ICON_SIZE} />
        <Text textColor="primary" size="100">
          {methodologyText}
        </Text>
      </Flex>
      {/* 3. Years of experience */}
      {shouldShowYearsOfExperience && (
        <Flex gap="1x" data-testid="years-of-experience-row">
          <Trophy width={ICON_SIZE} height={ICON_SIZE} />
          <Text textColor="primary" size="100">
            {yearsOfExperience}
          </Text>
        </Flex>
      )}
      {/* 4. Location */}
      {virtualOnly && (
        <Flex gap="1x" data-testid="virtual-only-row">
          <VirtualOnlyBadgeIcon iconSize={`${ICON_SIZE}px`} />
          <VirtualOnlyBadgeText fontSize="100" />
        </Flex>
      )}
      {shouldShowLocation && (
        <Flex gap="1x" data-testid="location-row">
          <Map width={ICON_SIZE} height={ICON_SIZE} />
          <Text textColor="primary" size="100">
            {location}
          </Text>
        </Flex>
      )}
    </Flex>
  );
};

export default TrainerStatsSection;
